import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Sidebar } from './components/layout/Sidebar';
import { TopBar } from './components/layout/TopBar';
import { MobileBottomNav } from './components/layout/MobileBottomNav';
import { LoginPage } from './components/auth/LoginPage';
import { ErrorBoundary, ToastContainer } from './components/ui';
import { KeyboardShortcuts } from './components/ui/KeyboardShortcuts';
import { OnboardingWizard, isOnboardingComplete } from './components/onboarding/OnboardingWizard';
import { useNotificationSocket } from './hooks/useNotificationSocket';
import { useLicenseFeaturesLoader, useLicenseFeature } from './hooks/useLicenseFeatures';
import { useAuthStore, useChatStore } from './stores';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { parsePath, pushRoute, replaceRoute, type Route } from './lib/route';
import { matchShortcut, pickRoom, roomListOrder } from './lib/shortcuts';
import { api } from './lib/api';
import { isPhoneViewport, useIsPhone, watchVisualViewport } from './lib/viewport';

/* ── Lazy-loaded views (route-level code splitting) ──────────── */
const ChatView = lazy(() => import('./components/chat/ChatView').then(m => ({ default: m.ChatView })));
const MeetView = lazy(() => import('./components/meet/MeetView').then(m => ({ default: m.MeetView })));
const FilesView = lazy(() => import('./components/files/FilesView').then(m => ({ default: m.FilesView })));
const TeamView = lazy(() => import('./components/teams/TeamView').then(m => ({ default: m.TeamView })));
const CalendarView = lazy(() => import('./components/calendar/CalendarView').then(m => ({ default: m.CalendarView })));
const TodayView = lazy(() => import('./components/today/TodayView').then(m => ({ default: m.TodayView })));
const SettingsView = lazy(() => import('./components/settings/SettingsView').then(m => ({ default: m.SettingsView })));
const IncomingCallModal = lazy(() => import('./components/call/IncomingCallModal').then(m => ({ default: m.IncomingCallModal })));
const ActiveCallOverlay = lazy(() => import('./components/call/ActiveCallOverlay').then(m => ({ default: m.ActiveCallOverlay })));
const WorkflowsView = lazy(() => import('./components/workflows/WorkflowsView').then(m => ({ default: m.WorkflowsView })));
const MailView = lazy(() => import('./components/mail/MailView').then(m => ({ default: m.MailView })));
const CompliancePage = lazy(() => import('./components/compliance/CompliancePage').then(m => ({ default: m.CompliancePage })));

export type View = 'today' | 'chat' | 'mail' | 'teams' | 'meet' | 'files' | 'calendar' | 'settings' | 'workflows' | 'compliance';

/** Translated name of a view, for the tab title, landmark label and announcements */
const viewLabelKey = (v: View) => (v === 'compliance' ? 'topbar.compliance' : `nav.${v}`);

/* ── Prefetch map: trigger import() on sidebar hover for instant navigation ── */
const viewImportMap: Record<View, () => Promise<unknown>> = {
  today:     () => import('./components/today/TodayView'),
  chat:      () => import('./components/chat/ChatView'),
  mail:      () => import('./components/mail/MailView'),
  teams:     () => import('./components/teams/TeamView'),
  meet:      () => import('./components/meet/MeetView'),
  files:     () => import('./components/files/FilesView'),
  calendar:  () => import('./components/calendar/CalendarView'),
  settings:  () => import('./components/settings/SettingsView'),
  workflows:   () => import('./components/workflows/WorkflowsView'),
  compliance:  () => import('./components/compliance/CompliancePage'),
};

export function prefetchView(view: View) {
  viewImportMap[view]?.();
}

/** Shared loading skeleton shown while lazy chunks load */
function ViewSkeleton() {
  const { t } = useTranslation();
  return (
    <div className="flex h-full items-center justify-center bg-nord-0">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-nord-8 border-t-transparent" />
        <span className="text-sm text-nord-muted">{t('common.loading', 'Loading…')}</span>
      </div>
    </div>
  );
}

export type PendingDm = {
  userId: string;
  displayName: string;
};

export function App() {
  const { t } = useTranslation();
  // The URL decides where we start; '/' and unknown paths open Today
  const [initialRoute] = useState<Route>(() => parsePath(window.location.pathname) ?? { view: 'today' });
  const [currentView, setCurrentView] = useState<View>(initialRoute.view);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pendingDm, setPendingDm] = useState<PendingDm | null>(null);
  const [pendingMeeting, setPendingMeeting] = useState<{ title: string; joinUrl: string } | null>(null);
  const { isAuthenticated, isLoading, setUser, setLoading } = useAuthStore();
  const [viewAnnouncement, setViewAnnouncement] = useState('');
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const mainRef = useRef<HTMLElement>(null);
  const isPhone = useIsPhone();

  // Phones: size the shell to the visible area so the keyboard never covers the composer
  useEffect(() => (isPhone ? watchVisualViewport() : undefined), [isPhone]);

  const handleNavigate = (view: View) => {
    setCurrentView(view);
    const roomId = view === 'chat' ? useChatStore.getState().activeRoomId ?? undefined : undefined;
    pushRoute({ view, roomId });
    setMobileMenuOpen(false); // Close mobile drawer on navigation
    setViewAnnouncement(i18n.t('a11y.navigatedTo', { view: i18n.t(viewLabelKey(view)) }));
  };

  const handleOpenDm = (userId: string, displayName: string) => {
    handleNavigate('chat');
    setPendingDm({ userId, displayName });
  };

  const handleJoinMeeting = (meetingInfo: { title: string; joinUrl: string }) => {
    setPendingMeeting(meetingInfo);
    handleNavigate('meet');
  };

  useNotificationSocket();
  // Which add-ons the license allows (hidden / locked controls); refreshed while signed in
  useLicenseFeaturesLoader();
  const mailHidden = useLicenseFeature('mail') === 'hidden';

  // ── URL sync ──────────────────────────────────────────────────
  // A room in the URL wins over whatever ChatView would pick first
  useEffect(() => {
    if (initialRoute.roomId) useChatStore.getState().setActiveRoom(initialRoute.roomId);
    replaceRoute(initialRoute);
  }, [initialRoute]);

  // Back/Forward
  useEffect(() => {
    const onPop = () => {
      const r = parsePath(window.location.pathname) ?? { view: 'today' as View };
      setCurrentView(r.view);
      setViewAnnouncement(i18n.t('a11y.navigatedTo', { view: i18n.t(viewLabelKey(r.view)) }));
      if (r.roomId && r.roomId !== useChatStore.getState().activeRoomId) {
        useChatStore.getState().setActiveRoom(r.roomId);
      } else if (r.view === 'chat' && !r.roomId && isPhoneViewport()) {
        // Phones: Back from a conversation lands on the room list
        useChatStore.getState().setActiveRoom(null);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Opening a room adds a history entry, so Back returns to the previous room
  const activeRoomId = useChatStore(s => s.activeRoomId);
  useEffect(() => {
    if (currentView !== 'chat' || !activeRoomId) return;
    const here = parsePath(window.location.pathname);
    // Phones: the room list is a screen of its own, so opening a room from it is a step Back undoes
    if (isPhone) {
      pushRoute({ view: 'chat', roomId: activeRoomId, fromList: here?.view === 'chat' && !here.roomId });
      return;
    }
    // Filling in the room for a bare /chat is not a new place, so don't add an entry
    const write = here?.view === 'chat' && !here.roomId ? replaceRoute : pushRoute;
    write({ view: 'chat', roomId: activeRoomId });
  }, [currentView, activeRoomId, isPhone]);

  useEffect(() => {
    document.title = `${t(viewLabelKey(currentView))} · Eurion`;
  }, [currentView, t]);

  // Global shortcuts (the list lives in lib/shortcuts.ts)
  const navigateRef = useRef(handleNavigate);
  const authedRef = useRef(isAuthenticated);
  authedRef.current = isAuthenticated;
  navigateRef.current = handleNavigate;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || !authedRef.current) return;
      const id = matchShortcut(e);
      if (!id) return;
      e.preventDefault();
      if (id === 'palette') window.dispatchEvent(new Event('eurion:palette'));
      else if (id === 'shortcuts') setShowShortcuts(s => !s);
      else if (id === 'settings') navigateRef.current('settings');
      else if (id === 'room:prev' || id === 'room:next' || id === 'room:nextUnread') {
        const { rooms, activeRoomId, setActiveRoom, updateRoom } = useChatStore.getState();
        const target = pickRoom(roomListOrder(rooms), activeRoomId, id);
        if (!target) return;
        navigateRef.current('chat');
        setActiveRoom(target);
        updateRoom(target, { unreadCount: 0 });
      }
      else navigateRef.current(id.slice(3) as View);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // On mount: validate any stored token by fetching /v1/auth/me
  useEffect(() => {
    const token = localStorage.getItem('eurion_token');
    if (!token) {
      setLoading(false);
      return;
    }
    api.setToken(token);
    // Decode JWT to get userId (sub claim), then fetch full profile
    try {
      const payload = JSON.parse(atob(token.split('.')[1]!));
      const userId = payload.sub as string;
      api.get<{ id: string; email: string; displayName: string; orgId: string; roles: string[] }>(`/v1/users/${userId}`)
        .then((res) => {
          if (res.success && res.data) {
            setUser(res.data);
          } else {
            api.setToken(null);
            setLoading(false);
          }
        })
        .catch(() => {
          api.setToken(null);
          setLoading(false);
        });
    } catch {
      api.setToken(null);
      setLoading(false);
    }
  }, [setUser, setLoading]);

  // Show onboarding wizard for first-time users
  const user = useAuthStore(s => s.user);
  useEffect(() => {
    if (user?.id && !isOnboardingComplete(user.id)) {
      setShowOnboarding(true);
    }
  }, [user?.id]);

  // Still validating stored token
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-nord-0">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-aurora-2 border-t-transparent" role="status" aria-label={t('common.loading', 'Loading…')} />
      </div>
    );
  }

  // Not authenticated — show login
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  const renderView = () => {
    switch (currentView) {
      case 'today':
        return <TodayView onNavigate={handleNavigate} onJoinMeeting={handleJoinMeeting} />;
      case 'chat':
        return <ChatView pendingDm={pendingDm} onDmOpened={() => setPendingDm(null)} />;
      case 'mail':
        // Not part of the license (hidden): no Mail view, e.g. from an old link or a shortcut
        return mailHidden ? <TodayView onNavigate={handleNavigate} onJoinMeeting={handleJoinMeeting} /> : <MailView />;
      case 'teams':
        return <TeamView />;
      case 'meet':
        return <MeetView autoJoinMeeting={pendingMeeting} onAutoJoinHandled={() => setPendingMeeting(null)} />;
      case 'files':
        return <FilesView />;
      case 'calendar':
        return <CalendarView onJoinMeeting={handleJoinMeeting} />;
      case 'settings':
        return <SettingsView />;
      case 'workflows':
        return <WorkflowsView />;
      case 'compliance':
        return <CompliancePage />;
    }
  };

  return (
    <ErrorBoundary>
      {/* Skip navigation link — visible on focus for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[99999] focus:rounded-lg focus:bg-nord-10 focus:px-4 focus:py-2 focus:text-nord-6 focus:font-medium focus:shadow-lg focus:outline-2 focus:outline-offset-2 focus:outline-nord-8"
      >
        {t('appShell.skipToMain', 'Skip to main content')}
      </a>

      {/* Screen-reader live region for view change announcements */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {viewAnnouncement}
      </div>

      <div className="flex h-screen overflow-hidden bg-nord-0 max-md:h-[var(--app-height,100dvh)] max-md:safe-top max-md:safe-x">
        <Sidebar
          currentView={currentView}
          onNavigate={handleNavigate}
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          mobileOpen={mobileMenuOpen}
          onMobileClose={() => setMobileMenuOpen(false)}
        />
        <div className="flex flex-1 flex-col overflow-hidden">
          <TopBar
            currentView={currentView}
            onNavigate={handleNavigate}
            onOpenDm={handleOpenDm}
            onMobileMenuToggle={() => setMobileMenuOpen(o => !o)}
            onShowShortcuts={() => setShowShortcuts(true)}
          />
          <main id="main-content" ref={mainRef} className="flex-1 overflow-hidden mobile-tabbar-space md:pb-0" aria-label={t(viewLabelKey(currentView))}>
            <ErrorBoundary>
              <Suspense fallback={<ViewSkeleton />}>
                {renderView()}
              </Suspense>
            </ErrorBoundary>
          </main>
          {/* Mobile bottom navigation bar */}
          <MobileBottomNav
            currentView={currentView}
            onNavigate={(view) => {
              // The Chat tab opens the room list, not the last conversation
              if (view === 'chat') useChatStore.getState().setActiveRoom(null);
              handleNavigate(view);
            }}
          />
        </div>
        <Suspense fallback={null}>
          <IncomingCallModal />
          <ActiveCallOverlay />
        </Suspense>
        <ToastContainer />
        <KeyboardShortcuts open={showShortcuts} onClose={() => setShowShortcuts(false)} />
        {showOnboarding && (
          <OnboardingWizard
            onClose={() => setShowOnboarding(false)}
            onNavigate={handleNavigate}
          />
        )}
      </div>
    </ErrorBoundary>
  );
}
